import { Injectable, OnDestroy } from '@angular/core';
import { AssetManager } from '@app/asset-manager/asset-manager';
import { ElementType } from '@app/platform/busines-data/Framework/Region';
import { AssetInfoViewService } from '@app/template/asset-info-view/asset-info-view/asset-info-view.service';
import { Subscription } from 'rxjs';
import { StructureTypesEventsService } from '@app/template/view-mode-tools/view-mode-anomaly-labels/structure-types-events.service';
import { AssetPropertiesLoaderService } from '@app/asset-manager/asset-properties-loader.service';
import { ELEMENTSTYPESIDS } from 'projects/phdsoft/common/src/lib/shared/constants/elementsTypesIds';

export interface ViewModeOption {
  id: string;
  name: string;
  type: 'toggle' | 'slider';
  value?: boolean | number;
  min?: number;
  max?: number;
  step?: number;
}

@Injectable({
  providedIn: 'root'
})
export class ViewModeElementsService implements OnDestroy {

  allStructuralTypesSorted: string[] = ['Plate', 'Stiffener', 'FacePlate', 'Bracket', 'BeamPart', 'Cable', 'Pipe', "PipeConnector", "Anode"];
  availableStructureElementTypes: ElementType[] = [];
  selectedElementsTypes: ElementType[] = [];
  
  private regionChangeSubscription: Subscription;
  private currentRegionIds: string[] = [];
  private isElementsViewModeActive: boolean = false;

  // Visual options
  visualOptions: ViewModeOption[] = [
    // {
    //   id: 'xray',
    //   name: 'X-Ray',
    //   type: 'toggle',
    //   value: false
    // },
    // {
    //   id: 'labels',
    //   name: 'Label',
    //   type: 'toggle',
    //   value: false
    // },
    // {
    //   id: 'wireframe',
    //   name: 'Wireframe',
    //   type: 'toggle',
    //   value: false
    // },
    // {
    //   id: 'transparency',
    //   name: 'Transparency',
    //   type: 'toggle',
    //   value: false
    // },
    // {
    //   id: 'opacity',
    //   name: 'Opacity',
    //   type: 'slider',
    //   value: 1.0,
    //   min: 0.1,
    //   max: 1.0,
    //   step: 0.1
    // }
  ];

  constructor(
    private assetManager: AssetManager,
    private assetInfoViewService: AssetInfoViewService,
    private structureTypesEventsService: StructureTypesEventsService,
  ) {
    // Subscribe to region changes to update visible elements automatically
    this.regionChangeSubscription = this.assetInfoViewService.castCurrentRegionIds.subscribe((regionIds: string[]) => {
      if (regionIds && regionIds.length > 0) {
        //filter duplicate regionids
        const uniqueRegionIds = [...new Set(regionIds)];
        this.handleRegionChange(uniqueRegionIds);
      }
    });
  }

  handleRegionChange(regionIds: string[]): void {
    const regionCountChanged = regionIds.length !== this.currentRegionIds.length;
    const regionContentChanged = !regionIds.every(id => this.currentRegionIds.includes(id));

    const hasRegionChanged = regionCountChanged || regionContentChanged;

    if (hasRegionChanged) {
      this.currentRegionIds = [...regionIds];
      this.refreshViewMode();
    } else {
      this.updateVisibleElements();
    }
  }

  filterFeaElementsByElementTypes(elementTypes: string[]): void {
    // Filter individual element meshes (SECTIONS mode)
    this.assetManager.asset.StructureCampaigns.forEach((structureCollection) => {
      structureCollection.Elements.forEach((element) => {
        if (elementTypes.includes(element.TypeId.toString())) {
          element.SurfaceDesign.visible = true;
        } else {
          element.SurfaceDesign.visible = false;
        }
      })
    })

    // Filter lightSection merged meshes (REGION mode)
    this.assetManager.filterLightSectionElementTypes(elementTypes);
  }

  updateVisibleElements(): void {
    // Store the currently selected element type IDs to preserve user selection
    const previouslySelectedIds = this.selectedElementsTypes.map(type => type.id);
    
    this.availableStructureElementTypes = [];
    
    for (const region of this.assetManager.asset.GetStructureVisibleRegions()) {
      for (const type of region.getRegionElementsTypes()) {
        if (!this.availableStructureElementTypes.some(existingType => existingType.id === type.id)) {
          this.availableStructureElementTypes.push(type);
        }
      }
    }
    
    this.availableStructureElementTypes.sort((a, b) => this.allStructuralTypesSorted.indexOf(a.name) - this.allStructuralTypesSorted.indexOf(b.name));
    
    // Restore the previous selection, but only for element types that are still available
    this.selectedElementsTypes = this.availableStructureElementTypes.filter(type => 
      previouslySelectedIds.includes(type.id)
    );
    
    if (this.isElementsViewModeActive) {
      this.filterFeaElementsByElementTypes(this.selectedElementsTypes.map(type => type.id));
      this.structureTypesEventsService.emitVisibleStructureTypesChange(this.selectedElementsTypes);
    }
  }

  selectAllStructureElements(availableElementTypes?: ElementType[]): ElementType[] {
    for (const region of this.assetManager.asset.GetStructureVisibleRegions()) {
      for (const type of region.getRegionElementsTypes()) {
        if (!this.availableStructureElementTypes.some(existingType => existingType.id === type.id)) {
          this.availableStructureElementTypes.push(type);
        }
      }
    }
    const elementsToSelect = availableElementTypes || this.availableStructureElementTypes;
    // TODO: temporary solution until the faceplates visualization is fixed
    const effectiveElements = this.isFaceplateHiddenByDefault
      ? elementsToSelect.filter(type => type.id !== ELEMENTSTYPESIDS.FACEPLATE)
      : elementsToSelect;
    if (effectiveElements.length > 0) {
      this.selectedElementsTypes = [...effectiveElements];
      this.filterFeaElementsByElementTypes(this.selectedElementsTypes.map(type => type.id));

      this.structureTypesEventsService.emitVisibleStructureTypesChange(this.selectedElementsTypes);
      return this.selectedElementsTypes;
    }
    this.selectedElementsTypes = [];
    this.structureTypesEventsService.emitVisibleStructureTypesChange(this.selectedElementsTypes);
    return this.selectedElementsTypes;
  }

  selectElementType(elementType: ElementType): void {
    if (this.selectedElementsTypes.includes(elementType)) {
      this.selectedElementsTypes = this.selectedElementsTypes.filter(type => type !== elementType);
    } else {
      this.selectedElementsTypes.push(elementType);
    }
    this.filterFeaElementsByElementTypes(this.selectedElementsTypes.map(type => type.id));
    
    this.structureTypesEventsService.emitVisibleStructureTypesChange(this.selectedElementsTypes);
  }

  toggleVisualOption(option: ViewModeOption): void {
    if (option.type === 'toggle') {
      option.value = !option.value;
      this.applyVisualOption(option);
    }
  }

  private applyVisualOption(option: ViewModeOption): void {
    switch (option.id) {
      // case 'xray':
      //   this.assetManager.toggleXRayInspectionViewMode(option.value as boolean);
      //   break;
      
      // case 'labels':
      //   if (option.value) {
      //     this.viewModeAnomalyLabelsService.activateAnomalyLabels();
      //   } else {
      //     this.viewModeAnomalyLabelsService.deactivateAnomalyLabels();
      //   }
      //   break;
      
      // case 'wireframe':
      //   this.toggleWireframe(option.value as boolean);
      //   break;
      
      // case 'transparency':
      //   if (option.value) {
      //     this.assetManager.changeToTransparenceViewMode();
      //   } else {
      //     this.assetManager.changeToDefaultViewMode();
      //   }
      //   break;
      
      // case 'opacity':
      //   this.setOpacity(option.value as number);
      //   break;
    }
  }

  private toggleWireframe(enabled: boolean): void {
    // TODO: Implement wireframe toggle functionality
    // This would be specific to FEA visualization
    console.log('Wireframe toggle:', enabled);
  }

  private setOpacity(value: number): void {
    // TODO: Implement opacity setting functionality
    // This would be specific to FEA visualization
    console.log('Opacity set to:', value);
  }

  refreshViewMode(): void {
    this.visualOptions.forEach(option => {
      if (option.type === 'toggle') {
        option.value = false;
      } else if (option.type === 'slider') {
        option.value = option.max || 1.0;
      }
      this.applyVisualOption(option);
    });

    this.updateVisibleElements();
    this.selectAllStructureElements();
  }

  /**
   * Activates elements view mode
   */
  activateElementsViewMode(): void {
    this.isElementsViewModeActive = true;
    this.refreshViewMode();
    // Labels are now controlled by the user through the "Label" option
  }

  /**
   * Deactivates elements view mode and restores structural element visibility
   */
  deactivateElementsViewMode(): void {
    this.isElementsViewModeActive = false;
    this.selectAllStructureElements();
  }

  /**
   * Restores elements view mode state from a detailed visualization mode string
   * Format: "ELEMENTS|elementIds"
   * Example: "ELEMENTS|1,2,3"
   */
  restoreElementsViewMode(visualizationMode: string): void {
    // Parse the detailed mode string
    const parts = visualizationMode.split('|');
    if (parts.length !== 2) {
      // Fallback to basic elements mode
      this.activateElementsViewMode();
      return;
    }

    const [baseMode, elementIdsStr] = parts;
    if (baseMode !== 'ELEMENTS') {
      // Fallback to basic elements mode
      this.activateElementsViewMode();
      return;
    }

    this.isElementsViewModeActive = true;
    this.updateVisibleElements();

    // Restore selected element types
    if (elementIdsStr && elementIdsStr.trim()) {
      const selectedElementIds = elementIdsStr.split(',').map(id => id.trim());
      this.selectedElementsTypes = this.availableStructureElementTypes.filter(type =>
        selectedElementIds.includes(type.id.toString())
      );

      // Apply the filtering
      this.filterFeaElementsByElementTypes(selectedElementIds);
      this.structureTypesEventsService.emitVisibleStructureTypesChange(this.selectedElementsTypes);
    } else {
      this.selectedElementsTypes = [];
      this.filterFeaElementsByElementTypes([]);
      this.structureTypesEventsService.emitVisibleStructureTypesChange(this.selectedElementsTypes);
    }
  }

  public reapplyElementsViewMode(): void {
    if (!this.isElementsViewModeActive) {
      return;
    }

    this.updateVisibleElements();
  }

  /**
   * Re-applies the CURRENT element-type selection on the lightSection type meshes after something
   * re-showed them (explorer region/section toggle → Section.setViewOnRegionMode shows every type
   * mesh, setViewOnModelMode re-shows the plate). Never recomputes availability or the selection:
   * an explorer region uncheck can leave no structure region visible, and updateVisibleElements /
   * selectAllStructureElements would then wipe the selection. Region-set changes keep going through
   * the castCurrentRegionIds subscription (handleRegionChange).
   * - Elements view mode active: the user's selection (an empty one included: the user's choice).
   * - Inactive on an FEA/submodel asset: the default selection made by selectAllStructureElements
   *   (faceplates hidden); skipped while none was made yet, so it never filters everything away.
   * - Otherwise a no-op.
   */
  public reapplyElementTypeFilter(): void {
    if (this.isElementsViewModeActive) {
      // was: this.reapplyElementsViewMode(); — updateVisibleElements recomputed the selection from the
      // visible regions, so a region uncheck (none visible) wiped it and the recheck hid every type
      this.filterFeaElementsByElementTypes(this.selectedElementTypeIds);
      return;
    }
    if (this.isFaceplateHiddenByDefault && this.selectedElementsTypes.length > 0) {
      // was: this.selectAllStructureElements();
      this.filterFeaElementsByElementTypes(this.selectedElementTypeIds);
    }
  }

  public get isActive(): boolean {
    return this.isElementsViewModeActive;
  }

  public get selectedElementTypeIds(): string[] {
    return this.selectedElementsTypes.map(type => type.id?.toString());
  }

  private get isFaceplateHiddenByDefault(): boolean {
    const assetInfo = this.assetManager.asset?.assetInfo;
    if (!assetInfo) return false;
    const isFea = assetInfo.IsCad === false && !!assetInfo.LinkedAsset;
    const isSubmodel = assetInfo.IsSubmodel === true && !!assetInfo.ParentFeaModelId;
    return isFea || isSubmodel;
  }

  ngOnDestroy(): void {
    if (this.regionChangeSubscription) {
      this.regionChangeSubscription.unsubscribe();
    }
  }

}
